import { useEffect, useState } from "react";
import { Button, Container, Dropdown, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsPencil, BsTrash } from "react-icons/bs";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { customStyles, formatDate, handleExportData } from "../../../Utils/helpers/commonhelper";
import "./LeadSoucre.css";
import {
  DeleteRequest,
  GetRequest,
  PutRequest,
} from "../../../Utils/hooks/ApiHandler";
import { toast } from "sonner";
import DeleteModal from "../../DeleteModal";
import useLocalStorageState from "use-local-storage-state";
import { TableSkeleton } from "../../../Utils/skeletonLoader/TableSkeleton";
import {
  useGetRequest,
  usePostRequest,
} from "../../../Utils/hooks/QueryHandler";
import Select from "react-select";
import nodata from "../../../Utils/assets/img/leads_no_data_found.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faFileExport, faFileImport } from "@fortawesome/free-solid-svg-icons";
import { DescriptionTooltip } from "../../../Utils/helpers/descriptionTooltip/DescriptionTooltip";
import settings_masters from "../../../Utils/assets/img/masterImage.png";

//  VALIDATION SCHEMA FOR FORM
const schema = z.object({
  name: z.string().min(1, "Lead source is required"),
  description: z.string().optional(),
  status: z.enum(["Active", "Inactive"], {
    required_error: "Status is required",
  }),
});

export const LeadSoucre = () => {
  // Getting customer info from session storage
  const [customer, setCustomer] = useLocalStorageState("customer");
  // Local states for managing designation data, errors, and loading state
  const navigate = useNavigate();
  const [leadSources, setLeadSources] = useState([]);
  const [editId, setEditId] = useState(null);
  //delete modal loading state
  const [loading, setLoading] = useState(false)

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // React Hook Form setup with Zod validation schema
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      status: "Active",
    },
  });

  // master permissions 
  const permission = customer.customer_details?.permission;
  const user_type = Number(customer.customer_details?.user_type)
  const settingsPermission = permission?.filter(c => c.module_name === "setting")
  // view permission for master 
  const masterGrant = {
    isView: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.delete)) : true,
  }
  const leadImportFields = [
    { id: "title", label: "Lead Source", required: true },
    { id: "description", label: "Description", required: false },
  ];

  // TOKEN HEADERS FOR API  Request
  const customerId = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    authentication: rf,
    Authorization: token,
  };

  const extras = { customer, setCustomer, rf, navigate };

  //GET LEAD SOURCE FROM API
  const {
    data: leadSourcesData,
    error,
    refetch: fetchLeadSource,
    isLoading,

    isError,
  } = useGetRequest(
    `masters/getAllLead_source/${customerId}`,
    "getLeadSource",

    headers,

    extras
  );
  // Fetch departments on component mount
  useEffect(() => {
    if (leadSourcesData?.status === "success") {
      setLeadSources(leadSourcesData.data);
    } else if (isError) {
      console.error("Error fetching roles:", error);
    }
  }, [leadSourcesData, isError]);

  //FORM SUBMISSION HANDLER FOR ADD / UPDATE

  const { mutate: addLead_source } = usePostRequest(
    `masters/addLead_source`,
    null,
    { headers },
    extras
  );

  const onSubmit = async (formData) => {
    if (editId === null && !masterGrant?.isView) {
      toast.warning("You are not authorized for this action!")
      return
    }
    const name = formData.name.trim();
    const description = formData.description?.trim() || "";
    const status = formData.status;
    // Check for duplicate name
    const isDuplicate = leadSources.some(
      (d) => d.source_title.trim() === name && d.id !== editId
    );
    if (isDuplicate) {
      toast.error("Duplicate LeadSource name ");
      return;
    }

    try {
      const payload = {
        customer_id: customerId,
        login_user_id: customer?.customer_details?.id,
        source_title: name,
        lead_source: name,
        description,
        status,
      };

      if (editId !== null) {
        payload.source_id = editId;

        const res = await PutRequest(
          `masters/updateLead_source`,
          payload,
          { headers },
          extras
        );

        if (res.status === "success") {
          await fetchLeadSource();
          setEditId(null);
          reset({ name: "", description: "", status: "Active" });
          toast.success(res.message);
        }
      } else {
        addLead_source(payload, {
          onSuccess: (res) => {
            if (res?.status === "success") {
              toast.success(res.message || "Role added successfully");
              // trigger refetch to fetch all roles again for displaying on UI after successfully adding the role
              fetchLeadSource();

              reset({ name: "", description: "", status: "Active" });
            } else {
              toast.error(res.message || "Failed to add role");
            }
          },
          onError: (err) => {
            toast.error(err?.message || "Add role request failed");
          },
        });
      }
    } catch (err) {
      console.error("Submit error:", err);
    }
  };
  //EDIT EXISTING ITEM WITH THE HELP OF API
  const onEdit = async (item) => {
    try {
      const res = await GetRequest(
        `masters/getDetailsOfLead_source/${item.id}`,
        { headers },
        extras
      );

      if (res?.status === "success" && Array.isArray(res.data)) {
        const data = res.data[0];

        setEditId(item.id);
        reset({
          name: data.source_title || "",
          description: data.description || "",
          status:
            data.status === "Active" || data.status === "Inactive"
              ? data.status
              : "Active",
        });
      }
    } catch (error) {
      console.error("Error loading details:", error);
    }
  };

  //Open delete confirmation modal
  const onDelete = (id) => {
    setLeadToDelete(id);
    setShowDeleteModal(true);
  };
  //Handle confirmed delete
  const handleConfirmedDelete = async (id) => {
    try {
      const item = leadSources.find((d) => d.id === id);

      if (!item) return;
      setLoading(true)
      const payload = {
        source_id: id,
        customer_id: customerId,
        designation_name: item.name,
        description: item.description,
        status: item.status,
      };

      const res = await DeleteRequest(
        `masters/deleteLead_source/${payload.source_id}`,
        { headers },
        extras
      );
      setShowDeleteModal(false);
      if (res.status === "success") {
        await fetchLeadSource();
        if (editId === id) {
          reset({ name: "", description: "", status: "Active" });
          setEditId(null);
        }
        setLoading(false)
        toast.success(res.message);
      } else {
        setLoading(false)
        toast.error(res.message);
      }
    } catch (error) {
      setLoading(false)
      console.error("Delete error:", error);
    }
  };
  return (
    <div className="container-fluid p-3 designation-container mb-5">
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleConfirmedDelete(leadToDelete)}
        message="Are you sure you want to delete this item?"
        isLoading={loading}
      />

      <div className="d-flex gap-5 justify-content-between align-items-center flex-wrap mb-3">
        <div style={{ width: "80%", paddingRight: "20px" }}>
          <h5 className="fw-semibold mb-1">Lead Sources </h5>
          <p className="text-muted small ">
            Define and manage various sources from which enquiries originate.
            example social media platforms, websites, referrals, advertisements,
            walk-ins, emails, and other marketing channels. This module helps
            businesses analyze lead generation effectiveness, optimize marketing
            efforts, and improve customer engagement strategies.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 ml-1">
          <Button
            className="btn btn-default-outline"
            onClick={() => navigate(-1)}
          >
            <BsArrowLeft className="me-1" /> Back
          </Button>
          <Dropdown>
            {/* <Dropdown.Toggle className="btn btn-default">
              Action

            </Dropdown.Toggle> */}
            <Dropdown.Toggle className=" btn btn-default no-arrow" style={{ display: "flex", alignItems: "center", paddingRight: "7px" }}>
              Action
              {/* <i className="bi bi-caret-down-fill d-inline-bl <i class="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>ock ps-1" ></i> */}
              <i class="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => {
                navigate("/importdata", {
                  state: {
                    module_name: "lead_source",
                    fieldData: [
                      {
                        title: "General Fields",
                        fields: leadImportFields.map((c) => c),
                      },
                    ],
                  },
                });
              }}><FontAwesomeIcon
                  icon={faFileImport}
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "9px",
                    color: "#5e5f60",
                  }}
                />{" "}
                Import Data</Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  handleExportData([], "lead_source", headers, extras);
                }}>
                <FontAwesomeIcon

                      icon={faFileExport}
                      style={{
                        width: "16px",
                        height: "16px",
                        marginRight: "9px",
                        color: "#5e5f60",
                      }}
                    />{" "}
                    Export Data</Dropdown.Item>
              {/* <Dropdown.Item>Copy from Other</Dropdown.Item>
              <Dropdown.Item>SVC Data</Dropdown.Item> */}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <hr
        className="mb-4"
        style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
      />      <div className="row">
        {/* Form Section */}
        <div className="col-md-4 mb-4">
          <div className="form-card">
            <div className="mb-3">
              <h6 className="fw-semibold mb-1">
                {editId ? "Edit Lead Source" : "Add Lead Source"}
              </h6>
              <p className="text-muted small mb-2">
                {editId
                  ? "Update the Lead Source below."
                  : "Add new Lead Source here."}
              </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="form-container">
              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label">
                  Enter Enquiry Source <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Ex. website"
                  {...register("name")}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name.message}</small>
                )}
              </div>
              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label textareaLables">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  {...register("description")}
                />
              </div>
              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label">Select Lead status</label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                      ]}
                      styles={customStyles}
                      classNamePrefix="react-select"
                      placeholder="Select status"
                      value={
                        field.value
                          ? { value: field.value, label: field.value }
                          : null
                      }
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption?.value) // object -> string
                      }}
                    />
                  )}
                />

                {errors.status && (
                  <small className="text-danger">{errors.message}</small>
                )}
              </div>
              <button type="submit" className="w-100 btn btn-default">
                {editId ? "Update" : "Submit"}
              </button>
            </form>
            <hr className="my-3" />
            <div className="text-center mt-4">
              <img
                src={settings_masters}
                alt="illustration"
                className="img-fluid"
                style={{ maxHeight: "180px" }}
              />
              <div className="mt-2">
                <h6 className="fw-semibold mb-1">Lead Source</h6>
                <p className="text-muted mb-0 small">
                  Define and manage various sources from which enquiries
                  originate. example social media platforms, websites,
                  referrals, advertisements, walk-ins, emails, and other
                  marketing channels. This module helps businesses analyze lead
                  generation effectiveness, optimize marketing efforts, and
                  improve customer engagement strategies.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Table Section */}
        <div className="col-md-8">
          <div className="table-card">
            <div className="d-flex justify-content-between align-items-start align-items-md-center flex-column flex-md-row">
              <div>
                <h6 className="fw-semibold mb-1">Lead Sources List</h6>
                <p className="text-muted small mb-2 mb-md-0">
                  List of current leads within your organization.
                </p>
              </div>
              {/* <div className="d-flex align-items-center gap-2 mt-1 mb-2 mt-md-0 border border-1 rounded-2 px-1 "> */}
              <div className=" position-relative mt-1 mb-2 mt-md-0">
                <input
                  disabled={!masterGrant?.isView}
                  type="search"
                  className="form-control "
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    fontSize: "12px",
                    paddingLeft: "36px",
                    height: "35px",
                  }}
                />
                <i
                  class="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    marginTop: "-26px",
                    zIndex: 1,
                    marginLeft: "13px",
                  }}
                ></i>
              </div>
            </div>
            {error ? (
              <div className="alert alert-danger" role="alert">
                {error?.message || "Something went wrong"}
              </div>
            ) : (
              <>
                {
                  masterGrant?.isView ?
                    <Container
                      fluid
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        height: "calc(100vh - 10rem)",
                        backgroundColor: "white",
                        padding: 0,
                        animation: "cardEntry 1s ease-out forwards",
                      }}
                      className="mt-3 p-0"
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          width: "100%",
                        }}
                      >

                        <div
                          className="listStyleForProductandService container-table"
                          style={{
                            flex: leadSourcesData?.length > 10 ? 1 : "auto",
                            overflow: "auto",
                            width: "100%",
                            maxHeight:
                              leadSourcesData?.length > 10 ? "100%" : "auto",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.scrollbarColor = "#e0e0e0 white"

                          }}
                        >
                          <Table
                            style={{
                              width: "100%",
                              tableLayout: "auto",
                              backgroundColor: "white",
                              marginBottom: "0px",
                            }}
                          >
                            <thead style={{ zIndex: "10" }}>
                              <tr>
                                <th>S.NO</th>
                                <th>NAME</th>
                                <th>DESCRIPTION</th>
                                <th>CREATED ON</th>
                                <th>STATUS</th>
                                <th>ACTION</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isLoading ? <TableSkeleton columnCount={5} /> :
                                <>
                                  {leadSources
                                    .filter((d) =>
                                      d.source_title

                                        .toLowerCase()
                                        .includes(searchTerm.trim().toLowerCase())
                                    )
                                    .map((d, idx) => (
                                      <tr key={d.id}>
                                        <td>{idx + 1}</td>
                                        {/* <td>{d.source_title}</td> */}
                                        {/* <td className="position-relative">
                              <span className="description-ellipsis-wrapper">
                                <span className="description-ellipsis">
                                  {d.description.length > 20
                                    ? `${d.description.substring(0, 20)}...`
                                    : d.description}
                                </span>
                                <div className="description-hover-box">
                                  {d.description}
                                </div>
                              </span>
                            </td> */}
                                        <td className="td_position">
                                          <DescriptionTooltip
                                            text={d.source_title}
                                            maxLength={20}
                                          />

                                        </td>
                                        <td>
                                          <DescriptionTooltip
                                            text={d.description}
                                            maxLength={20}
                                          />
                                        </td>

                                        <td>{formatDate(d.created_at)}</td>

                                        <td>
                                          <div
                                            className={` ${d.status === "Active"
                                              ? "btn btn-sm btn-primary-light"
                                              : "btn btn-sm btn-danger-light"
                                              }`}
                                          >
                                            {d.status}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="d-flex gap-2">
                                            <Button
                                              size="sm"
                                              variant="outline-primary"
                                              onClick={() => {
                                                if (!masterGrant?.isEdit) {
                                                  toast.warning("You are not authorized for this action!")
                                                  return
                                                }
                                                onEdit(d)
                                              }}
                                              title="Edit"
                                            >
                                              <BsPencil />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline-danger"
                                              onClick={() => {
                                                if (!masterGrant?.isDelete) {
                                                  toast.warning("You are not authorized for this action!")
                                                  return
                                                }
                                                onDelete(d.id)
                                              }
                                              }

                                              title="Delete"
                                            >
                                              <BsTrash />
                                            </Button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  {/*  if no data found on search then it will run  */}
                                  {leadSources.filter((d) =>
                                    d.source_title

                                      .toLowerCase()
                                      .includes(searchTerm.trim().toLowerCase())
                                  ).length === 0 && (
                                      <td
                                        colSpan="14"
                                        style={{ textAlign: "center", color: "gray" }}
                                      >
                                        <div
                                          class="d-flex justify-content-center align-items-center"
                                          style={{ position: "absolute", left: " 30%" }}
                                        >
                                          <img
                                            src={nodata}
                                            alt=""
                                            style={{ width: "17rem", marginTop: "0" }}
                                          />
                                        </div>
                                      </td>
                                    )}
                                </>
                              }
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Container>
                    :
                    <>You are not authorized to view Lead Source listing!</>
                }

              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
};
